import { inject, Injectable } from '@angular/core';
import { oofDtos, smbApi, smbDtos } from '@vectronic/openApi-admin';
import { dsApi, dsDtos } from '@vectronic/openApi-customer';
import { catchError, EMPTY, forkJoin, from, Observable, switchMap } from 'rxjs';

/**
 * Generates GPS+X schedule XML files from any structured schedules
 * (formly field type: 'schedule') stored in the formData and stores them
 * on the file server via the Pasiphae SMB service.
 */
@Injectable({ providedIn: 'root' })
export class ProductionScheduleFilesService {
  private readonly scheduleApi = inject(dsApi.Schedules);
  private readonly generatedScheduleFilesApi = inject(smbApi.PasiphaeGeneratedScheduleFiles);

  /**
   * Generates and stores GPS+X schedule files for the given order item configuration.
   * If files already exist (e.g. on a production restart) they will be overwritten.
   */
  createAndStoreScheduleFiles({
    config,
    order,
  }: {
    config: oofDtos.OrderItemConfiguration;
    order: oofDtos.Order;
  }): Observable<smbDtos.Result[]> {
    const schedules = this.extractSchedules(config.formData);

    if (schedules.length === 0) return EMPTY;

    return forkJoin(
      schedules.map(({ schedule, scheduleType }) => {
        const singleSchedule: dsDtos.SingleSchedule = { schedule };
        const cameraDeviceTypeId =
          scheduleType === dsDtos.Schedule.ScheduleTypeEnum.Camera
            ? this.resolveCameraDeviceTypeId(config.formData)
            : undefined;
        const fileName = this.buildFileName(
          config.formData.prioListNo,
          config.configNo,
          order.fakturaId,
          scheduleType,
        );

        return this.scheduleApi.toXmlString(singleSchedule, cameraDeviceTypeId).pipe(
          switchMap((blob) => from((blob as unknown as Blob).text())),
          switchMap((xmlContent) =>
            this.generatedScheduleFilesApi.storeScheduleFile(fileName, { content: xmlContent }),
          ),
        );
      }),
    ).pipe(
      catchError((err) => {
        console.error('Failed to create GPS+X schedule files on production start', err);
        return EMPTY;
      }),
    );
  }

  private extractSchedules(
    formData: oofDtos.OrderItemConfigurationFormData,
  ): { schedule: dsDtos.Schedule; scheduleType: dsDtos.Schedule.ScheduleTypeEnum }[] {
    const results: { schedule: dsDtos.Schedule; scheduleType: dsDtos.Schedule.ScheduleTypeEnum }[] =
      [];

    const walk = (value: unknown): void => {
      if (value == null || typeof value !== 'object' || Array.isArray(value)) return;
      if (this.isSchedule(value)) {
        results.push({ schedule: value, scheduleType: value.scheduleType });
        return;
      }
      for (const child of Object.values(value as Record<string, unknown>)) {
        walk(child);
      }
    };

    walk(formData);
    return results;
  }

  private isSchedule(value: unknown): value is dsDtos.Schedule {
    return (
      value != null &&
      typeof value === 'object' &&
      'scheduleType' in value &&
      (value as dsDtos.Schedule).scheduleType != null
    );
  }

  /**
   * Builds the GPS+X schedule file name.
   * Format: {prioListNo}-{configNo}_{processId}_{scheduleType}.vgsf
   * Example: 23-1_NA2252_GPS.vgsf
   */
  private buildFileName(
    prioListNo: number | undefined,
    configNo: number | undefined,
    processId: string | undefined,
    scheduleType: dsDtos.Schedule.ScheduleTypeEnum,
  ): string {
    const prioListPart =
      prioListNo != null && configNo != null
        ? `${prioListNo}-${configNo}`
        : (prioListNo ?? configNo)?.toString();
    const parts = [prioListPart, processId, scheduleType.toUpperCase()].filter((p) => p != null);
    return `${parts.join('_')}.vgsf`;
  }

  private resolveCameraDeviceTypeId(
    formData: oofDtos.OrderItemConfigurationFormData,
  ): string | undefined {
    return (formData.context?.['cameraDeviceTypeId'] as unknown as string) ?? undefined;
  }
}
